/**
 * Guarda de permiso para las pantallas de Administración (Sedes, Atendidos,
 * Configuración).
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (mismo enfoque que `RequirePermission` de Equipo o `RequireManageTalonarios`).
 * Replica la paridad con el SSR (`docs/frontend_architecture.md`, Pr. 5): toda la
 * administración es solo-admin (`@require_admin` -> permiso `all`); sin ese
 * permiso se muestra un aviso en vez del contenido.
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { ADMIN_PERMISSION } from "../constants";

interface RequireAdminProps {
  /** Título del aviso de "sin permiso". */
  title: string;
  /** Mensaje del aviso de "sin permiso". */
  deniedMessage: string;
  children: ReactNode;
}

export function RequireAdmin({ title, deniedMessage, children }: RequireAdminProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar el aviso "sin permiso" en ese instante sería engañoso. Se
  // espera a tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(ADMIN_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            {deniedMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
